// ==================================
// File: src/layouts/AdminLayout.jsx
// ==================================
import { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  List,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";

import { useTheme } from "@mui/material/styles";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import SidebarMenu from "../components/SidebarMenu";
import { useAuth } from "../context/AuthContext";
import { io } from "socket.io-client";

const drawerWidth = 240;
const appBarHeight = 64;

export default function AdminLayout() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  // ✅ FIXED: proper state + setter
  const [unreadContacts, setUnreadContacts] = useState(0);

  const socketRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const displayName = user?.full_name || "Admin";

  const toggleDrawer = () => setMobileOpen((prev) => !prev);

  /* =====================================================
     ✅ SOCKET — CORRECT & ESLINT-SAFE
  ===================================================== */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const sessionToken = localStorage.getItem("sessionToken");

    // 🔒 HARD GUARDS
    if (!token || !sessionToken) return;
    if (user?.role !== "admin") return;

    socketRef.current = io(
      import.meta.env.VITE_API_URL || "https://api.cbt-master.com.ng",
      {
        autoConnect: false,
        auth: {
          token,
          sessionToken,
        },
      }
    );

    socketRef.current.on("connect", () => {
      socketRef.current.emit("admin:contacts:subscribe");
    });

    socketRef.current.on("admin:contacts:unread-count", (count) => {
      setUnreadContacts(Number(count) || 0);
    });

    socketRef.current.on("connect_error", (err) => {
      console.error("AdminLayout socket error:", err.message);
    });

    socketRef.current.connect();

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  /* =====================================================
     MENU ITEMS
  ===================================================== */
  const items = [
    { label: "Dashboard", to: "/admin", icon: "dashboard" },
    {
      label: unreadContacts > 0
        ? `Contact Inbox (${unreadContacts})`
        : "Contact Inbox",
      to: "/admin/contacts",
      icon: "mail",
    },
    { label: "Manage Users", to: "/admin/users", icon: "users" },
    { label: "Subjects", to: "/admin/subjects", icon: "subjects" },
    { label: "Upload Questions", to: "/admin/questions", icon: "practice" },
    { label: "Manage Questions", to: "/admin/manage-questions", icon: "practice" },
    { label: "English Questions", to: "/admin/english", icon: "subjects" },
    { label: "Challenges", to: "/admin/weekly-leaderboards", icon: "leaderboard" },
    { label: "Live Exams", to: "/admin/live-exams", icon: "exam" },
    { label: "User Session", to: "/admin/sessions", icon: "history" },
    { label: "Trials", to: "/admin/trials", icon: "payment" },
    { label: "Subscriptions", to: "/admin/subscriptions", icon: "wallet" },
    { label: "Agent Payout", to: "/admin/payouts", icon: "payouts" },
    { label: "Agents", to: "/admin/agents", icon: "agents" },
    { label: "Logout", to: "/logout", icon: "logout" },
  ];

  const drawer = (
    <>
      <Box sx={{ height: appBarHeight }} />
      <Divider />
      <List sx={{ p: 1 }}>
        <SidebarMenu
          items={items}
          activePath={location.pathname}
          onNavigate={(to) => {
            if (to === "/logout") return logout();
            navigate(to);
            setMobileOpen(false);
          }}
        />
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* ---------- TOP APP BAR ---------- */}
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          height: appBarHeight,
          justifyContent: "center",
          zIndex: (t) => t.zIndex.drawer + 2,
        }}
      >
        <Toolbar sx={{ minHeight: appBarHeight, px: 2 }}>
          {!mdUp && (
            <IconButton edge="start" onClick={toggleDrawer} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}

          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            CBT Nova — Admin Panel
          </Typography>

          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <AccountCircleIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              Welcome, {displayName}
            </Typography>
            <IconButton aria-label="logout" onClick={logout}>
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ---------- LEFT DRAWER ---------- */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={toggleDrawer}
          ModalProps={{
            keepMounted: false,
            disableScrollLock: true,
            hideBackdrop: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": { width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* ---------- MAIN CONTENT ---------- */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Box sx={{ height: appBarHeight }} />
        <Outlet />
      </Box>
    </Box>
  );
}
